/**
 * Single source of truth for the Pass Analytics Graph palette.
 *
 * Each entry binds three identifiers together:
 *   - `value`       — stable id used by the selector and the chart card key
 *   - `sourceField` — `WeldTelemetrySampleRow` field name to pull raw values from
 *   - `paramKey`    — key passed to `weldUnitUtils.convertWeldDataValue` /
 *                     `getParameterUnitSuffix` (uses the same names as the
 *                     Table tab, slice 03)
 *
 * The colour palette and labels mirror the Pass Analytics graph elsewhere in
 * the org so cross-app screenshots match. `arc_impedance` is intentionally
 * omitted because `weld_telemetry_samples` has no source column for it.
 */
import type { WeldTelemetrySampleRow } from './weldTelemetryClient'

export interface GraphParameterDef {
  /** Stable identifier used by the selector and as a chart key. */
  value: string
  /** Human-facing label without unit suffix. */
  label: string
  /** Palette colour (six-digit hex). */
  color: string
  /** Sample-row field to read the raw numeric value from. */
  sourceField: keyof WeldTelemetrySampleRow
  /** Parameter key for `convertWeldDataValue` / `getParameterUnitSuffix`. */
  paramKey: string
}

export const GRAPH_PARAMETERS: GraphParameterDef[] = [
  { value: 'travel_speed', label: 'Travel Speed', color: '#EB1C37', sourceField: 'travelSpeed', paramKey: 'travelSpeed' },
  { value: 'volts', label: 'Volts', color: '#FFA500', sourceField: 'volts', paramKey: 'volts' },
  { value: 'current', label: 'Current', color: '#ee82ee', sourceField: 'amps', paramKey: 'amps' },
  { value: 'wire_speed', label: 'Wire Speed', color: '#800080', sourceField: 'wireIpm', paramKey: 'wireIpm' },
  { value: 'oscillation_width', label: 'Oscillate Width', color: '#A52A2A', sourceField: 'widthMil', paramKey: 'widthMil' },
  { value: 'target', label: 'Target', color: '#808080', sourceField: 'targetVlt', paramKey: 'targetVlt' },
  { value: 'horizontal_bias', label: 'Horizontal Bias', color: '#00FFFF', sourceField: 'csBias', paramKey: 'csBias' },
  { value: 'frequency', label: 'Frequency', color: '#FFC0CB', sourceField: 'bpm', paramKey: 'bpm' },
  { value: 'total_wire_consumed', label: 'Total Wire Consumed', color: '#FFFF00', sourceField: 'totWireIn', paramKey: 'totWireIn' },
  { value: 'true_energy', label: 'True Energy', color: '#0000FF', sourceField: 'trueEnergy', paramKey: 'trueEnergy' },
  { value: 'heat', label: 'Heat Input', color: '#008000', sourceField: 'rprtHeat', paramKey: 'rprtHeat' }
]

export const DEFAULT_SELECTED_PARAMETERS: string[] = ['travel_speed', 'volts']
